// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { logValidationError, logError } from '../../utils/errorLogging';

// Validation rule types
export interface ValidationRule {
  type: 'required' | 'min' | 'max' | 'range' | 'pattern' | 'custom';
  value?: number | string | RegExp;
  min?: number;
  max?: number;
  message: string;
  severity: 'error' | 'warning' | 'info';
  validator?: (value: any) => boolean;
}

export interface FieldValidation {
  fieldName: string;
  rules: ValidationRule[];
  guidance?: string;
  example?: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  warnings: ValidationError[];
  fieldStates: Record<string, FieldState>;
}

export interface ValidationError {
  field: string;
  message: string;
  severity: 'error' | 'warning' | 'info';
  rule: string;
}

export interface FieldState {
  isValid: boolean;
  isTouched: boolean;
  errors: ValidationError[];
  warnings: ValidationError[];
  hasBeenValidated: boolean;
}

// Validation engine class
export class ValidationEngine {
  private validations: Map<string, FieldValidation> = new Map();
  
  registerField(validation: FieldValidation) {
    this.validations.set(validation.fieldName, validation);
  }
  
  validateField(fieldName: string, value: any, allData?: any): FieldState {
    const validation = this.validations.get(fieldName);
    if (!validation) {
      return {
        isValid: true,
        isTouched: true,
        errors: [],
        warnings: [],
        hasBeenValidated: true,
      };
    }
    
    const errors: ValidationError[] = [];
    const warnings: ValidationError[] = [];
    
    try {
      for (const rule of validation.rules) {
        const result = this.executeRule(rule, value, allData);
        if (!result.passed) {
          const error: ValidationError = {
            field: fieldName,
            message: rule.message,
            severity: rule.severity,
            rule: rule.type,
          };
          
          if (rule.severity === 'error') {
            errors.push(error);
            // Log validation errors for monitoring
            logValidationError(fieldName, value, rule.type, 'ValidationEngine');
          } else {
            warnings.push(error);
          }
        }
      }
    } catch (error) {
      // Log unexpected validation errors
      logError(error as Error, {
        component: 'ValidationEngine',
        action: 'FieldValidation',
        additionalData: { fieldName, value, hasValidation: !!validation }
      });
      
      // Return error state
      errors.push({
        field: fieldName,
        message: 'Validation system error occurred',
        severity: 'error',
        rule: 'system'
      });
    }
    
    return {
      isValid: errors.length === 0,
      isTouched: true,
      errors,
      warnings,
      hasBeenValidated: true,
    };
  }
  
  validateAll(data: any): ValidationResult {
    const fieldStates: Record<string, FieldState> = {};
    const allErrors: ValidationError[] = [];
    const allWarnings: ValidationError[] = [];
    
    for (const [fieldName] of this.validations) {
      const fieldState = this.validateField(fieldName, data[fieldName], data);
      fieldStates[fieldName] = fieldState;
      allErrors.push(...fieldState.errors);
      allWarnings.push(...fieldState.warnings);
    }
    
    return {
      isValid: allErrors.length === 0,
      errors: allErrors,
      warnings: allWarnings,
      fieldStates,
    };
  }
  
  private executeRule(rule: ValidationRule, value: any, allData?: any): { passed: boolean } {
    switch (rule.type) {
      case 'required':
        return { passed: value !== null && value !== undefined && value !== '' };
        
      case 'min':
        return { passed: value >= (rule.value as number) };
        
      case 'max':
        return { passed: value <= (rule.value as number) };
        
      case 'range':
        return { passed: value >= rule.min! && value <= rule.max! };
        
      case 'pattern':
        return { passed: (rule.value as RegExp).test(String(value)) };
        
      case 'custom':
        return { passed: rule.validator ? rule.validator(value) : true };
        
      default:
        return { passed: true };
    }
  }
  
  getFieldGuidance(fieldName: string): string | undefined {
    return this.validations.get(fieldName)?.guidance;
  }
  
  getFieldExample(fieldName: string): string | undefined {
    return this.validations.get(fieldName)?.example;
  }
}

// React hook for validation
export function useValidation(validations: FieldValidation[]) {
  const [engine] = useState(() => {
    const validationEngine = new ValidationEngine();
    validations.forEach(validation => validationEngine.registerField(validation));
    return validationEngine;
  });
  
  const [fieldStates, setFieldStates] = useState<Record<string, FieldState>>({});
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());
  
  const validateField = (fieldName: string, value: any, allData?: any) => {
    const fieldState = engine.validateField(fieldName, value, allData);
    
    setFieldStates(prev => ({
      ...prev,
      [fieldName]: fieldState,
    }));
    
    return fieldState;
  };
  
  const validateAll = (data: any) => {
    const result = engine.validateAll(data);
    setFieldStates(result.fieldStates);
    
    // Mark all fields as touched
    setTouchedFields(new Set(Object.keys(result.fieldStates)));
    
    return result;
  };
  
  const markFieldTouched = (fieldName: string) => {
    setTouchedFields(prev => new Set([...prev, fieldName]));
  };
  
  const getFieldState = (fieldName: string): FieldState | undefined => {
    return fieldStates[fieldName];
  };
  
  const getFieldGuidance = (fieldName: string) => {
    return engine.getFieldGuidance(fieldName);
  };
  
  const getFieldExample = (fieldName: string) => {
    return engine.getFieldExample(fieldName);
  };
  
  const isFieldTouched = (fieldName: string) => {
    return touchedFields.has(fieldName);
  };
  
  const clearFieldState = (fieldName: string) => {
    setFieldStates(prev => {
      const newState = { ...prev };
      delete newState[fieldName];
      return newState;
    });
    setTouchedFields(prev => {
      const newSet = new Set(prev);
      newSet.delete(fieldName);
      return newSet;
    });
  };
  
  return {
    validateField,
    validateAll,
    markFieldTouched,
    getFieldState,
    getFieldGuidance,
    getFieldExample,
    isFieldTouched,
    clearFieldState,
    fieldStates,
    hasErrors: Object.values(fieldStates).some(state => state.errors.length > 0),
    hasWarnings: Object.values(fieldStates).some(state => state.warnings.length > 0),
  };
}

// Enhanced input component with validation
interface ValidatedInputProps {
  fieldName: string;
  label: string;
  value: any;
  onChange: (value: any) => void;
  validation: ReturnType<typeof useValidation>;
  type?: 'text' | 'number' | 'email' | 'tel';
  placeholder?: string;
  disabled?: boolean;
  showGuidance?: boolean;
  className?: string;
}

export const ValidatedInput: React.FC<ValidatedInputProps> = ({
  fieldName,
  label,
  value,
  onChange,
  validation,
  type = 'text',
  placeholder,
  disabled = false,
  showGuidance = true,
  className = '',
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const fieldState = validation.getFieldState(fieldName);
  const isFieldTouched = validation.isFieldTouched(fieldName);
  const guidance = validation.getFieldGuidance(fieldName);
  const example = validation.getFieldExample(fieldName);
  
  const hasErrors = fieldState?.errors.length > 0;
  const hasWarnings = fieldState?.warnings.length > 0;
  const showValidationFeedback = isFieldTouched && fieldState?.hasBeenValidated;
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value;
    onChange(newValue);
    
    // Validate on change if field has been touched
    if (isFieldTouched) {
      validation.validateField(fieldName, newValue);
    }
  };
  
  const handleBlur = () => {
    setIsFocused(false);
    validation.markFieldTouched(fieldName);
    validation.validateField(fieldName, value);
  };
  
  const handleFocus = () => {
    setIsFocused(true);
  };
  
  const getInputClassName = () => {
    let classes = ['validated-input', className];
    
    if (showValidationFeedback) {
      if (hasErrors) {
        classes.push('validation-error');
      } else if (hasWarnings) {
        classes.push('validation-warning');
      } else if (fieldState?.isValid) {
        classes.push('validation-success');
      }
    }
    
    if (isFocused) {
      classes.push('focused');
    }
    
    if (disabled) {
      classes.push('disabled');
    }
    
    return classes.join(' ');
  };
  
  return (
    <div className="validated-input-container">
      <label className="validated-input-label">
        {label}
        {fieldState?.errors.some(e => e.rule === 'required') && (
          <span className="required-indicator">*</span>
        )}
      </label>
      
      <div className="input-wrapper">
        <input
          type={type}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          placeholder={placeholder}
          disabled={disabled}
          className={getInputClassName()}
          aria-invalid={hasErrors}
          aria-describedby={`${fieldName}-guidance ${fieldName}-errors`}
        />
        
        {/* Validation status indicator */}
        <div className="validation-indicator">
          {showValidationFeedback && (
            <>
              {hasErrors && <span className="error-icon">⚠️</span>}
              {!hasErrors && hasWarnings && <span className="warning-icon">⚡</span>}
              {!hasErrors && !hasWarnings && fieldState?.isValid && (
                <span className="success-icon">✅</span>
              )}
            </>
          )}
        </div>
      </div>
      
      {/* Guidance text */}
      {showGuidance && (isFocused || !isFieldTouched) && guidance && (
        <div id={`${fieldName}-guidance`} className="field-guidance">
          <span className="guidance-icon">💡</span>
          {guidance}
          {example && (
            <div className="guidance-example">
              <strong>Example:</strong> {example}
            </div>
          )}
        </div>
      )}
      
      {/* Error messages */}
      {showValidationFeedback && hasErrors && (
        <div id={`${fieldName}-errors`} className="validation-errors">
          {fieldState?.errors.map((error, index) => (
            <div key={index} className={`validation-message ${error.severity}`}>
              <span className="error-icon">❌</span>
              {error.message}
            </div>
          ))}
        </div>
      )}
      
      {/* Warning messages */}
      {showValidationFeedback && hasWarnings && !hasErrors && (
        <div className="validation-warnings">
          {fieldState?.warnings.map((warning, index) => (
            <div key={index} className={`validation-message ${warning.severity}`}>
              <span className="warning-icon">⚠️</span>
              {warning.message}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Form validation summary component
interface ValidationSummaryProps {
  validation: ReturnType<typeof useValidation>;
  showOnlyErrors?: boolean;
}

export const ValidationSummary: React.FC<ValidationSummaryProps> = ({
  validation,
  showOnlyErrors = false,
}) => {
  const allErrors = Object.values(validation.fieldStates)
    .flatMap(state => state.errors);
  const allWarnings = Object.values(validation.fieldStates)
    .flatMap(state => state.warnings);
  
  if (allErrors.length === 0 && (showOnlyErrors || allWarnings.length === 0)) {
    return null;
  }
  
  return (
    <div className="validation-summary">
      {allErrors.length > 0 && (
        <div className="validation-summary-section errors">
          <h4>❌ Please fix the following errors:</h4>
          <ul>
            {allErrors.map((error, index) => (
              <li key={index}>
                <strong>{error.field}:</strong> {error.message}
              </li>
            ))}
          </ul>
        </div>
      )}
      
      {!showOnlyErrors && allWarnings.length > 0 && (
        <div className="validation-summary-section warnings">
          <h4>⚠️ Warnings:</h4>
          <ul>
            {allWarnings.map((warning, index) => (
              <li key={index}>
                <strong>{warning.field}:</strong> {warning.message}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};